import { useState } from 'react';
import { ADMIN_RECEIPT_OPTIONS } from '../constants/receiptOptions';
import { formatModelCodeForDisplay } from '../utils/modelCategoryRanges';
import { getOrderQtyLine, getPaymentSublabel, getWholesaleQtyParts } from '../utils/orderTimeline';

const OrderListRow = ({
  order,
  rowIndex = 0,
  expanded,
  onToggle,
  readOnly = false,
  safeRender,
  formatDate,
  getDisplayStatus,
  getStatusText,
  onDispatchRetail,
  onDispatchWholesale,
  onCancel,
  onEdit,
  onDeleteCancelled,
  onMarkBalanceReceived,
  onWholesaleBalanceReceived,
  onDownloadReceipt,
  onImageClick,
}) => {
  const [receiptOpen, setReceiptOpen] = useState(false);

  const status = safeRender(order.status);
  const type = safeRender(order.type);
  const balance = safeRender(order.balance, 0);
  const grandTotal = safeRender(order.grandTotal, 0);
  const totalAdvance = safeRender(order.totalAdvance, 0);
  const items = Array.isArray(order.items) ? order.items : [];

  const orderType = String(order?.type ?? '').trim().toLowerCase();
  const isRetail = orderType === 'retail';
  const isWholesale = orderType === 'wholesale';

  const qtyLine = getOrderQtyLine(order, items);
  const wholesaleQtyParts = isWholesale ? getWholesaleQtyParts(items) : null;
  const paymentSublabel = getPaymentSublabel(order);
  const typeLabel = type ? type.charAt(0).toUpperCase() + type.slice(1) : '—';
  const statusLabel = getStatusText(order);
  const displayStatus = getDisplayStatus(order);
  const statusTextColorClass = {
    pending: 'text-yellow-800',
    completed: 'text-green-800',
    cancelled: 'text-red-800',
    dispatched: 'text-blue-800',
  }[displayStatus] || 'text-gray-800';

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  const stopPropagation = (event) => event.stopPropagation();

  const resolveItemModel = (item) => {
    if (!item) return { name: '', number: '', size: '' };
    const name = item.modelName || item.model?.modelName || '';
    const number = item.modelNumber ?? item.model?.modelNumber ?? '';
    const size = item.model?.size || '';
    return {
      name: String(name).trim(),
      number: String(number).trim(),
      size: String(size).trim(),
    };
  };

  const formatItemModelLabel = (item) => {
    const { name, number, size } = resolveItemModel(item);
    if (!name && !number) return null;
    const code = number ? formatModelCodeForDisplay(number, size || undefined) : '';
    return code ? `${name || 'Model'} · ${code}` : name;
  };

  const renderWholesaleQty = () => {
    if (!wholesaleQtyParts) return '—';
    return (
      <>
        <span className="order-wholesale-qty-count">{wholesaleQtyParts.count}</span>
        <span className="order-wholesale-qty-label">{wholesaleQtyParts.label}</span>
      </>
    );
  };

  const renderReceiptControl = () => (
    <div className="relative w-full h-full" onClick={stopPropagation}>
      <button
        type="button"
        onClick={(e) => { stopPropagation(e); setReceiptOpen((prev) => !prev); }}
        className="order-smart-action-btn order-smart-action-btn--secondary admin-btn-neutral w-full"
      >
        Receipt ▾
      </button>
      {receiptOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setReceiptOpen(false)} aria-hidden="true" />
          <div className="receipt-sheet md:hidden" role="menu">
            <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Download receipt</p>
            {ADMIN_RECEIPT_OPTIONS.map(({ lang, format, label }) => (
              <button
                key={`${lang}-${format}-sheet`}
                type="button"
                role="menuitem"
                onClick={() => { onDownloadReceipt(order._id, order.orderNo, lang, format); setReceiptOpen(false); }}
                className="receipt-sheet-item"
              >
                {label}
              </button>
            ))}
          </div>
          <div className={`hidden md:block receipt-menu ${rowIndex < 2 ? 'receipt-menu-down' : 'receipt-menu-down-sm-up'}`}>
            {ADMIN_RECEIPT_OPTIONS.map(({ lang, format, label }) => (
              <button
                key={`${lang}-${format}`}
                type="button"
                onClick={() => { onDownloadReceipt(order._id, order.orderNo, lang, format); setReceiptOpen(false); }}
                className="receipt-menu-item"
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderPrimaryGridAction = () => {
    if (readOnly) {
      if (status === 'cancelled') {
        return <span className="order-smart-action-label text-gray-500">Cancelled</span>;
      }
      return null;
    }

    if (status === 'pending') {
      if (isRetail) {
        return (
          <div className="flex w-full h-full gap-1 min-w-0">
            <button
              type="button"
              onClick={(e) => { stopPropagation(e); onEdit?.(order); }}
              className="order-smart-action-btn order-smart-action-btn--secondary admin-btn-info flex-1 min-w-0"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={(e) => { stopPropagation(e); onDispatchRetail(order); }}
              className="order-smart-action-btn order-smart-action-btn--primary admin-btn-success flex-1 min-w-0"
            >
              Dispatch
            </button>
          </div>
        );
      }
      if (isWholesale) {
        return (
          <button
            type="button"
            onClick={(e) => { stopPropagation(e); onDispatchWholesale(order); }}
            className="order-smart-action-btn order-smart-action-btn--primary admin-btn-success w-full"
          >
            Dispatch
          </button>
        );
      }
    }

    if (status === 'completed' && isRetail && balance > 0) {
      return (
        <button
          type="button"
          onClick={(e) => { stopPropagation(e); onMarkBalanceReceived(order._id); }}
            className="order-smart-action-btn order-smart-action-btn--primary admin-btn-warning w-full"
        >
          Balance Paid
        </button>
      );
    }

    if (status === 'dispatched' && isWholesale && balance > 0) {
      return (
        <button
          type="button"
          onClick={(e) => { stopPropagation(e); onWholesaleBalanceReceived(order); }}
            className="order-smart-action-btn order-smart-action-btn--primary admin-btn-warning w-full"
        >
          Balance Received
        </button>
      );
    }

    if (status === 'completed' && balance === 0) {
      return <span className="order-smart-action-label text-green-700">Fully Paid</span>;
    }

    if (status === 'cancelled') {
      return <span className="order-smart-action-label text-gray-500">Cancelled</span>;
    }

    if (status === 'dispatched' && balance <= 0) {
      return <span className="order-smart-action-label text-indigo-700">Dispatched</span>;
    }

    return <span className="order-smart-action-label text-gray-400">—</span>;
  };

  const renderExpandedFooter = () => {
    if (readOnly) {
      if (status === 'cancelled') {
        return (
          <div className="order-expanded-footer" onClick={stopPropagation}>
            {totalAdvance > 0 && (
              <span className="order-smart-action-btn bg-amber-50 text-amber-800 border border-amber-200">
                Advance retained {formatMoney(totalAdvance)}
              </span>
            )}
            {onDeleteCancelled && (
              <button
                type="button"
                onClick={(e) => { stopPropagation(e); onDeleteCancelled(order); }}
                className="order-action-btn admin-btn-danger"
              >
                Delete order
              </button>
            )}
          </div>
        );
      }
      return null;
    }

    if (status !== 'pending') return null;

    return (
      <div className="order-expanded-footer" onClick={stopPropagation}>
        <button
          type="button"
          onClick={(e) => { stopPropagation(e); onCancel(order); }}
          className="order-action-btn admin-btn-danger"
        >
          Cancel order
        </button>
      </div>
    );
  };

  const gridClass = 'order-smart-grid order-smart-grid--no-customer hidden md:grid';

  const renderPaymentPill = () => {
    if (paymentSublabel === '—') return null;
    return (
      <span className={`order-smart-payment-pill ${paymentSublabel === 'Due' ? 'order-smart-payment-pill--due' : 'order-smart-payment-pill--paid'}`}>
        {paymentSublabel}
      </span>
    );
  };

  return (
    <div className="order-row last:border-b-0">
      <div
        className="px-3 sm:px-4 py-3 md:py-2.5 cursor-pointer"
        onClick={() => onToggle(order._id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(order._id); }}
      >
        <div className="order-mobile-card md:hidden">
          <div className="order-mobile-header">
            <div className="order-mobile-header-left min-w-0">
              <span className={`order-smart-chevron transform transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true">▼</span>
              <span className="order-mobile-order-no truncate">{safeRender(order.orderNo)}</span>
              {order.source === 'shop' && (
                <span className="order-smart-shop-pill shrink-0">Shop</span>
              )}
            </div>
            <span className="order-mobile-amount shrink-0">{formatMoney(grandTotal)}</span>
          </div>

          <div className="order-mobile-meta">
            <div className="order-mobile-meta-left min-w-0 truncate" title={qtyLine}>
              <span className="capitalize font-medium">{typeLabel}</span>
              <span className="text-gray-500">
                {' · '}
                {isWholesale ? (
                  <span className="order-wholesale-qty">{renderWholesaleQty()}</span>
                ) : (
                  qtyLine
                )}
              </span>
            </div>
            <div className="order-mobile-meta-right shrink-0">
              <span className={`capitalize font-semibold ${statusTextColorClass}`}>{statusLabel}</span>
              {renderPaymentPill()}
            </div>
          </div>

          {balance > 0 && (
            <p className="order-mobile-advance text-gray-500">Advance {formatMoney(totalAdvance)}</p>
          )}

          <div className="order-mobile-actions" onClick={stopPropagation}>
            <div className="order-smart-action-slot">{renderPrimaryGridAction()}</div>
            <div className="order-smart-action-slot">{renderReceiptControl()}</div>
          </div>
        </div>

        <div className={gridClass}>
          <div className="order-smart-cell order-smart-cell--order">
            <div className="order-smart-primary">
              <span className={`order-smart-chevron transform transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true">▼</span>
              <span className="order-smart-order-no">{safeRender(order.orderNo)}</span>
            </div>
            <div className="order-smart-secondary">
              {order.source === 'shop' ? (
                <span className="order-smart-shop-pill">Shop</span>
              ) : (
                '\u00A0'
              )}
            </div>
          </div>

          <div className="order-smart-cell order-smart-cell--type min-w-0">
            <span className="order-smart-primary capitalize">{typeLabel}</span>
            {isWholesale ? (
              <span className="order-smart-secondary order-wholesale-qty" title={qtyLine}>
                {renderWholesaleQty()}
              </span>
            ) : (
              <span className="order-smart-secondary truncate" title={qtyLine}>{qtyLine}</span>
            )}
          </div>

          <div className="order-smart-cell order-smart-cell--status">
            <span className={`order-smart-primary capitalize font-semibold ${statusTextColorClass}`}>
              {statusLabel}
            </span>
            <span className="order-smart-secondary">
              {paymentSublabel === '—' ? (
                '\u00A0'
              ) : (
                <span className={`order-smart-payment-pill ${paymentSublabel === 'Due' ? 'order-smart-payment-pill--due' : 'order-smart-payment-pill--paid'}`}>
                  {paymentSublabel}
                </span>
              )}
            </span>
          </div>

          <div className="order-smart-cell order-smart-cell--amount">
            <span className="order-smart-primary font-semibold text-gray-900">{formatMoney(grandTotal)}</span>
            <span className="order-smart-secondary">
              {balance > 0 ? `Adv ${formatMoney(totalAdvance)}` : '\u00A0'}
            </span>
          </div>

          <div className="order-smart-cell order-smart-cell--actions" onClick={stopPropagation}>
            <div className="order-smart-action-slot order-smart-action-slot--primary">{renderPrimaryGridAction()}</div>
            <div className="order-smart-action-slot order-smart-action-slot--secondary">{renderReceiptControl()}</div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-3 sm:px-4 pb-4 pt-3 border-t border-gray-100 bg-gray-50/30">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <h5 className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Photos</h5>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {items.map((item, index) => {
                  const { name, number, size } = resolveItemModel(item);
                  const itemLabel = formatItemModelLabel(item);
                  return (
                    <div key={index} className="bg-white p-1.5 rounded-md border">
                      {safeRender(item.photo) && (
                        <img
                          src={safeRender(item.photo)}
                          alt={itemLabel || safeRender(item.photo)}
                          className="w-full aspect-square object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                          crossOrigin="anonymous"
                          onClick={() => onImageClick({
                            src: safeRender(item.photo),
                            alt: name || itemLabel || 'Product',
                            modelNumber: number ? formatModelCodeForDisplay(number, size || undefined) : '',
                          })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-1">
              <h5 className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Summary</h5>
              <div className="space-y-1.5">
                <div className="bg-blue-50 p-2 rounded-md text-center">
                  <p className="text-[10px] text-blue-800 font-medium uppercase">Total</p>
                  <p className="text-sm font-bold text-blue-900">{formatMoney(grandTotal)}</p>
                </div>
                <div className="bg-green-50 p-2 rounded-md text-center">
                  <p className="text-[10px] text-green-800 font-medium uppercase">Advance</p>
                  <p className="text-sm font-bold text-green-900">{formatMoney(totalAdvance)}</p>
                </div>
                <div className={`p-2 rounded-md text-center ${balance === 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-[10px] font-medium uppercase ${balance === 0 ? 'text-green-800' : 'text-red-800'}`}>Balance</p>
                  <p className={`text-sm font-bold ${balance === 0 ? 'text-green-900' : 'text-red-900'}`}>{formatMoney(balance)}</p>
                </div>
                <p className="text-[10px] text-gray-500 text-center pt-1">{formatDate(order.createdAt)}</p>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h5 className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Items</h5>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {items.map((item, index) => (
                  <div key={index} className="bg-white border border-indigo-100 p-2 rounded-md">
                    <div className="text-xs font-semibold text-indigo-900">
                      {formatItemModelLabel(item) || 'Model details unavailable'}
                    </div>
                    {isWholesale && (
                      <div className="text-xs text-gray-700 mt-0.5 flex justify-between">
                        <span>Qty: {safeRender(item.qty)}</span>
                        <span>{formatMoney(item.totalValue)}</span>
                      </div>
                    )}
                    {isRetail && (
                      <div className="text-xs text-blue-600 font-medium mt-0.5">{formatMoney(item.unitPrice)}</div>
                    )}
                    {safeRender(item.colorRequest) && (
                      <div className="text-xs text-purple-700 mt-1 bg-purple-50 px-1.5 py-0.5 rounded">
                        Color: {safeRender(item.colorRequest)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {renderExpandedFooter()}
        </div>
      )}
    </div>
  );
};

export default OrderListRow;
